import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNotificationCount } from '@/hooks/useNotificationCount';

/* ── Icons ─────────────────────────────────────────────────────────────── */
function HomeIcon({ filled }: { filled?: boolean }) {
  return filled ? (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  ) : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12L12 3l9 9M5 10v10h5v-5h4v5h5V10" />
    </svg>
  );
}

function FollowingIcon({ filled }: { filled?: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'} strokeWidth="2">
      <path d="M20 8H4V6h16v2zm-2-6H6v2h12V2zm4 10v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-8c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2zm-6 4l-6-3.27v6.53L16 16z"
        fill={filled ? 'currentColor' : 'none'}
        stroke={filled ? 'none' : 'currentColor'}
      />
    </svg>
  );
}

function NotificationsIcon({ filled }: { filled?: boolean }) {
  return filled ? (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
  ) : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function SettingsIcon({ filled }: { filled?: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" opacity={filled ? 1 : 0.6}>
      <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
    </svg>
  );
}

/* ── Component ──────────────────────────────────────────────────────────── */
interface MobileNavProps {
  onUploadClick?: () => void;
}

export function MobileNav({ onUploadClick }: MobileNavProps) {
  const location = useLocation();
  const { user } = useCurrentUser();
  const { count: notifCount } = useNotificationCount();

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const navItemClass = cn(
    'flex flex-col items-center justify-center flex-1 gap-0.5 no-underline transition-colors min-h-0 py-2',
  );

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[200] md:hidden flex items-stretch"
      style={{
        background: 'var(--bg-secondary)',
        borderTop: '1px solid var(--gb-border)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        height: 'calc(56px + env(safe-area-inset-bottom))',
      }}
      aria-label="Mobile navigation"
    >
      {/* Home */}
      <Link
        to="/"
        aria-label="Home"
        aria-current={isActive('/') ? 'page' : undefined}
        className={navItemClass}
        style={{ color: isActive('/') ? 'var(--gb-accent)' : 'var(--text-secondary)' }}
      >
        <HomeIcon filled={isActive('/')} />
        <span className="text-[9px] font-medium leading-none">Home</span>
      </Link>

      {/* Following */}
      <Link
        to="/following"
        aria-label="Following"
        aria-current={isActive('/following') ? 'page' : undefined}
        className={navItemClass}
        style={{ color: isActive('/following') ? 'var(--gb-accent)' : 'var(--text-secondary)' }}
      >
        <FollowingIcon filled={isActive('/following')} />
        <span className="text-[9px] font-medium leading-none">Following</span>
      </Link>

      {/* ── Create (centre pill) ── */}
      <div className="flex items-center justify-center flex-1">
        <button
          onClick={onUploadClick}
          aria-label="Upload video"
          className="flex items-center justify-center rounded-xl transition-all active:scale-95"
          style={{
            width: 48,
            height: 36,
            background: user ? 'var(--gb-accent)' : 'var(--gb-border)',
            color: '#fff',
            boxShadow: user ? '0 2px 12px rgba(58,158,58,0.45)' : 'none',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
        </button>
      </div>

      {/* Notifications */}
      <Link
        to="/notifications"
        aria-label="Notifications"
        aria-current={isActive('/notifications') ? 'page' : undefined}
        className={navItemClass}
        style={{ color: isActive('/notifications') ? 'var(--gb-accent)' : 'var(--text-secondary)' }}
      >
        <span className="relative">
          <NotificationsIcon filled={isActive('/notifications')} />
          {notifCount > 0 && (
            <span
              className="absolute -top-1 -right-1 flex items-center justify-center rounded-full text-white font-bold"
              style={{
                minWidth: 16,
                height: 16,
                fontSize: 9,
                background: '#f44336',
                padding: '0 3px',
                lineHeight: '16px',
              }}
            >
              {notifCount > 99 ? '99+' : notifCount}
            </span>
          )}
        </span>
        <span className="text-[9px] font-medium leading-none">Alerts</span>
      </Link>

      {/* Settings */}
      <Link
        to="/settings"
        aria-label="Settings"
        aria-current={isActive('/settings') ? 'page' : undefined}
        className={navItemClass}
        style={{ color: isActive('/settings') ? 'var(--gb-accent)' : 'var(--text-secondary)' }}
      >
        <SettingsIcon filled={isActive('/settings')} />
        <span className="text-[9px] font-medium leading-none">Settings</span>
      </Link>
    </nav>
  );
}
